import { selectorFamily, waitForAll } from 'recoil';
import { compact, isNil, last, uniq, uniqWith, zip } from 'lodash';
import {
  getRequestSubjectFromAnalysisSubject,
  getSubjectFromRequestSubject,
  hasDisabledBenchmark,
  hasDisabledIndividualBenchmark,
  hasDisabledRelativeToBenchmark,
  hasFeesAndExcludedInvestments,
  isHistoricalSubject,
} from 'venn-utils';
import moment from 'moment/moment';
import { shouldHideBenchmark } from '../../../venn-components/src/blocks/utils';
import type { BlockId, StudioRequestSubject } from '../studio/types';
import { hasVennDataModifications, isReturnsBasedBlockState, viewPages } from '../studio/configuration/print';
import { isReportState } from '../studio/configuration/view';
import { rangeDebugQuery } from '../studio/async/rangeDebug';
import {
  type BlockAsOfDateSettingIndices,
  isProFormaBlockOrMetricBenchmark,
  subjectAsOfDate,
  subjectAsOfDateWithExplicitSubjectIndex,
} from '../studio/configuration/historicalAssets';
import { hasProxyState } from '../studio/proxy';
import {
  blockBenchmarkConfig,
  blockCustomizableMetricsMap,
  blockDoesNotSupportBenchmark,
  blockInfoGraphicType,
  blockLimitedRequestSubjects,
  blockSelectedCustomizableMetrics,
  customizedBlock,
} from '../studio/configuration/blockConfig';
import { blockCustomMetricSettingsState, blockSettings } from '../studio/configuration/blockSettings';
import { getBenchmarkSubject } from '../studio/configuration/subjects';
import { getAppTitle, getTextThemeProvider } from 'venn-ui-kit';

const footerTypes = [
  'date',
  'forward',
  'outdated',
  'proxy',
  'excluded-fees',
  'custom-metrics-or-metadata',
  'venn-modified',
] as const;
type FooterType = (typeof footerTypes)[number];

interface BlockSubjectsAndBenchmarks {
  subjects: StudioRequestSubject[];
  benchmarks: StudioRequestSubject[];
}

export interface BlockSubjectFooterState {
  blockId: BlockId;
  subject: StudioRequestSubject;
  isBenchmark: boolean;
  type: FooterType;
  allocationsAsOfDate: number | undefined;
  message: (organizationName?: string) => string;
  symbol: string;
  showSuperscript: boolean;
}

export const PROXY_DISCLAIMER =
  'Proxy investment applied to metrics where applicable—see Disclosure Page for more details.';
export const ALLOCATIONS_SNAPPED_MESSAGE =
  'End of period allocations were not found. Allocations closest to end of period were used.';
const OUTDATED_ALLOCATIONS_MESSAGE =
  'Most recent updated portfolio allocations are more than 6 months out of date and have drifted.';
export const VENN_DATA_MODIFICATIONS_DISCLAIMER =
  'Certain investments have cash flow data modifications applied due to missing or outdated data; See Disclosure page for additional details';

export const EXCLUDED_FEES_DISCLAIMER =
  'Certain investments are excluded from the stated advisory fees; some investments’ returns are already presented net of fees, while other investments are not subject to advisory fees. See Disclosure page for additional details.';

export const USER_DATA_DISCLAIMER = (organizationDisplayName?: string) =>
  `This data has been added by ${organizationDisplayName} and as such, ${getAppTitle()}${getTextThemeProvider().tsParam} is not responsible for its accuracy.`;

const OUTDATED_WARNING_THRESHOLD_MONTHS = 6;

/**
 * Internal use only selector, intended for: 'block-subject-footnotes-and-superscripts' to implement the hooks needed  for report lab
 */
export const pageFootNoteDetails = selectorFamily<BlockSubjectFooterState[], number>({
  key: 'pageFootNoteDetails',
  get:
    (pageNumber) =>
    ({ get }) => {
      const subjectFooterStates = get(pageBlocksSubjectFooterStates(pageNumber));
      return uniqWith(
        [...subjectFooterStates].sort((a, b) => {
          const typeDiff = footerTypes.indexOf(a.type) - footerTypes.indexOf(b.type);
          return typeDiff !== 0 ? typeDiff : (a.allocationsAsOfDate ?? 0) - (b.allocationsAsOfDate ?? 0);
        }),
        (a, b) => a.type === b.type && a.allocationsAsOfDate === b.allocationsAsOfDate,
      );
    },
});

/**
 * Internal use only selector, intended for: 'block-subject-footnotes-and-superscripts' to implement the hooks needed  for report lab
 */
export const blockSubjectFooterStates = selectorFamily<BlockSubjectFooterState[], BlockId>({
  key: 'blockSubjectFooterStates',
  get:
    (blockId) =>
    ({ get }) => {
      const pageNumber = get(viewPages).findIndex((page) => page.layout.some((layout) => layout.i === blockId));
      const states =
        pageNumber >= 0
          ? get(pageBlocksSubjectFooterStates(pageNumber))
          : get(internalOnlyBlocksSubjectFooterStates([blockId]));
      return states.filter((state) => state.blockId === blockId);
    },
});

const pageBlocksSubjectFooterStates = selectorFamily<BlockSubjectFooterState[], number>({
  key: 'pageBlocksSubjectFooterStates',
  get:
    (pageNumber) =>
    ({ get }) => {
      const pages = get(viewPages);
      const page = pages[pageNumber];
      if (!page) {
        return [];
      }
      const ids = page.layout.map((layout) => layout.i);
      return get(internalOnlyBlocksSubjectFooterStates(ids));
    },
});

const internalOnlyBlocksSubjectFooterStates = selectorFamily<BlockSubjectFooterState[], BlockId[]>({
  key: 'internalOnlyBlocksSubjectFooterStates',
  get:
    (ids) =>
    ({ get }) => {
      const allSubjects = get(
        waitForAll(ids.map((id) => filteredBlockSubjectsAndBenchmarks_exportedForTestingOnly(id))),
      );
      const allBlockModifications = get(waitForAll(ids.map((id) => hasVennDataModifications(id))));
      const isReportLab = get(isReportState);

      const blockSubjectsMap: {
        [key: string]: BlockSubjectsAndBenchmarks;
      } = Object.fromEntries(zip(ids, allSubjects));
      const blockModificationsMap: {
        [key: string]: BlockSubjectsAndBenchmarks;
      } = Object.fromEntries(zip(ids, allBlockModifications));

      const result = [];

      for (const blockId of ids) {
        const isReturnsBasedBlock = get(isReturnsBasedBlockState(blockId));
        const hasModifications = blockModificationsMap[blockId];
        const customBlockType = get(blockSettings(blockId)).customBlockType;
        const filteredSubjectState = blockSubjectsMap[blockId];
        const blocksCustomMetrics = get(blockCustomMetricSettingsState(blockId));
        const blocksCustomizableMetrics = get(blockSelectedCustomizableMetrics(blockId));

        const hasCustomMetrics = blocksCustomMetrics.length !== 0;
        const hasCustomMetadata = blocksCustomizableMetrics.some((metric) => metric.analysisType === 'CUSTOM_METADATA');

        if (!filteredSubjectState) {
          continue;
        }

        const states = [
          {
            subjects: filteredSubjectState.subjects,
            isBenchmark: false,
          },
          { subjects: filteredSubjectState.benchmarks, isBenchmark: true },
        ];
        for (const { subjects, isBenchmark } of states) {
          const hasExcludedFees = subjects.some(hasFeesAndExcludedInvestments);

          for (const [index, subject] of subjects.entries()) {
            const isHistorical = isHistoricalSubject(subject);
            if (isHistorical && isReportLab) {
              const rawSubject = getSubjectFromRequestSubject(subject);
              const rangeDebug = get(rangeDebugQuery(rawSubject));
              const historicalPortfolioMetadata = rangeDebug?.historicalPortfolioMetadata;
              let asOfDate;
              if (customBlockType === 'PORTFOLIO_COMPARISON' && index >= 0 && index < 2) {
                // Portfolio comparison is the only block that uses two as of date selectors (one for each subject)
                // so we need to handle it differently here
                asOfDate = get(
                  subjectAsOfDateWithExplicitSubjectIndex([blockId, rawSubject, index as BlockAsOfDateSettingIndices]),
                );
              } else {
                asOfDate = get(subjectAsOfDate([blockId, rawSubject]));
              }
              let useProFormaForHistoricalBenchmarksBlock = get(isProFormaBlockOrMetricBenchmark(blockId));
              useProFormaForHistoricalBenchmarksBlock = isBenchmark && useProFormaForHistoricalBenchmarksBlock;

              const isForwardFilled = !!historicalPortfolioMetadata?.isAllocationsForwardFilled;
              const outdatedAllocations = historicalPortfolioMetadata
                ? shouldShowAllocationsOutdatedWarning(
                    asOfDate,
                    historicalPortfolioMetadata.allSortedAllocationDates,
                    isBenchmark,
                    useProFormaForHistoricalBenchmarksBlock,
                  )
                : false;

              if (asOfDate) {
                result.push({
                  blockId,
                  subject,
                  isBenchmark,
                  type: 'date' as const,
                  allocationsAsOfDate: asOfDate,
                  message: () => `Allocations as of ${formatDate(asOfDate)}`,
                  showSuperscript: true,
                });
              }
              if (isForwardFilled) {
                result.push({
                  blockId,
                  subject,
                  isBenchmark,
                  type: 'forward' as const,
                  allocationsAsOfDate: undefined,
                  message: () => ALLOCATIONS_SNAPPED_MESSAGE,
                  showSuperscript: true,
                });
              }
              if (outdatedAllocations) {
                result.push({
                  blockId,
                  subject,
                  isBenchmark,
                  type: 'outdated' as const,
                  allocationsAsOfDate: undefined,
                  message: () => OUTDATED_ALLOCATIONS_MESSAGE,
                  showSuperscript: true,
                });
              }
            }

            const hasProxy = isReturnsBasedBlock ? get(hasProxyState(subject.id)) : false;
            if (hasProxy) {
              result.push({
                blockId,
                subject,
                isBenchmark,
                type: 'proxy' as const,
                allocationsAsOfDate: undefined,
                message: () => PROXY_DISCLAIMER,
                symbol: undefined, // if we want a non-number change back to: FootnoteSymbols.proxyUsage
                showSuperscript: true,
              });
            }

            if (hasExcludedFees) {
              result.push({
                blockId,
                subject,
                isBenchmark,
                type: 'excluded-fees' as const,
                allocationsAsOfDate: undefined,
                message: () => EXCLUDED_FEES_DISCLAIMER,
                showSuperscript: false,
              });
            }
            if (hasCustomMetrics || hasCustomMetadata) {
              result.push({
                blockId,
                subject,
                isBenchmark,
                type: 'custom-metrics-or-metadata' as const,
                allocationsAsOfDate: undefined,
                message: (organizationName?: string) => USER_DATA_DISCLAIMER(organizationName),
                showSuperscript: false,
              });
            }
            if (hasModifications) {
              result.push({
                blockId,
                subject,
                isBenchmark,
                type: 'venn-modified' as const,
                allocationsAsOfDate: undefined,
                message: () => VENN_DATA_MODIFICATIONS_DISCLAIMER,
                showSuperscript: false,
              });
            }
          }
        }
      }

      const uniqueSymbols = Object.fromEntries(
        uniqWith(
          [...result].sort((a, b) => {
            const typeDiff = footerTypes.indexOf(a.type) - footerTypes.indexOf(b.type);
            return typeDiff !== 0 ? typeDiff : (a.allocationsAsOfDate ?? 0) - (b.allocationsAsOfDate ?? 0);
          }),
          (a, b) => a.type === b.type && a.allocationsAsOfDate === b.allocationsAsOfDate,
        ).map((note, index) => [`${note.type}|${note.allocationsAsOfDate ?? 0}`, note.symbol ?? `${index + 1}`]),
      );

      return result.map((note) => ({
        ...note,
        symbol: uniqueSymbols[`${note.type}|${note.allocationsAsOfDate ?? 0}`] ?? 'DIDNT FIND SYMBOL',
      }));
    },
});

/**
 * DO NOT USE OUTSIDE OF THIS FILE
 */
export const filteredBlockSubjectsAndBenchmarks_exportedForTestingOnly = selectorFamily<
  BlockSubjectsAndBenchmarks,
  BlockId
>({
  key: 'filteredBlockSubjectsAndBenchmarks_exportedForTestingOnly',
  get:
    (id) =>
    ({ get }) => {
      const benchmarkConfig = get(blockBenchmarkConfig(id));
      const { customBlockType, relativeToBenchmark } = get(blockSettings(id));
      const infoGraphicType = get(blockInfoGraphicType(id));

      const subjects = get(blockLimitedRequestSubjects(id));
      const hasIndividualBenchmarks =
        benchmarkConfig.type === 'INDIVIDUAL' && !hasDisabledIndividualBenchmark(customBlockType);

      const metricDefinitionsMap = get(blockCustomizableMetricsMap(id));
      const { selectedMetrics } = get(customizedBlock(id));

      const metric = selectedMetrics[0] || '';

      const isBlockRelativeToBenchmark =
        relativeToBenchmark &&
        !hasDisabledBenchmark(customBlockType, infoGraphicType) &&
        !hasDisabledRelativeToBenchmark(customBlockType);

      const requiresBenchmark = isBlockRelativeToBenchmark || (metric && metricDefinitionsMap[metric]?.needsBenchmark);
      const hideBenchmark =
        get(blockDoesNotSupportBenchmark(id)) ||
        shouldHideBenchmark(customBlockType, metricDefinitionsMap, selectedMetrics);

      return {
        subjects: uniq(compact(subjects)),
        benchmarks: uniq(
          compact([
            (!hideBenchmark || requiresBenchmark) && benchmarkConfig?.subject
              ? getRequestSubjectFromAnalysisSubject(benchmarkConfig?.subject)
              : undefined,
            ...((!hideBenchmark || requiresBenchmark) && hasIndividualBenchmarks
              ? subjects.map((subject) =>
                  subject.individualBenchmark
                    ? getRequestSubjectFromAnalysisSubject(getBenchmarkSubject(subject.individualBenchmark))
                    : undefined,
                )
              : []),
          ]),
        ),
      };
    },
});

export const shouldShowAllocationsOutdatedWarning = (
  selectedAsOfDate: number | undefined,
  allSortedAllocationDates: number[],
  isBenchmark?: boolean,
  useProFormaForHistoricalBenchmarksBlock?: boolean,
) => {
  if (!selectedAsOfDate || (isBenchmark && !useProFormaForHistoricalBenchmarksBlock)) {
    return false;
  }

  const lastReportedAllocationDate = last(allSortedAllocationDates);

  if (!lastReportedAllocationDate || lastReportedAllocationDate > selectedAsOfDate) {
    return false;
  }

  // check if the gap between selected as of date and last reported allocation date is 6+ months
  return (
    moment(selectedAsOfDate).diff(moment(lastReportedAllocationDate), 'months') >= OUTDATED_WARNING_THRESHOLD_MONTHS
  );
};

const formatDate = (date?: number | null) => (!isNil(date) ? moment.utc(date).format('DD MMM YYYY') : undefined);
